import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDate, createUrlSlug } from "./functions";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "./firebase";
import { useSnackbar } from "./SnackbarHandler";
import RubricsDialog from "./RubricsDialog";

import ShareDialog from "./ShareDialog";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import LoadingBackdrop from "./LoadingBackdrop";

const MyRubricsTable = ({ rubrics, onRubricsUpdated, user }) => {
  const [editingRubric, setEditingRubric] = useState(null);
  const [viewingRubric, setViewingRubric] = useState(null);
  const [sharingRubric, setSharingRubric] = useState(null);
  const { showSnackbar } = useSnackbar();
  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareFriendsDialogOpen, setShareFriendsDialogOpen] = useState(false);

  const handleDelete = async (rubricId) => {
    if (
      window.confirm(
        "Är du säker på att du vill ta bort denna bedömningsmatris?",
      )
    ) {
      try {
        await deleteDoc(doc(db, "rubrics", rubricId));
        showSnackbar("Bedömningsmatris borttagen", "success");
        onRubricsUpdated();
      } catch (error) {
        console.error("Error deleting rubric:", error);
        showSnackbar(
          "Ett fel uppstod vid borttagning av bedömningsmatrisen",
          "error",
        );
      }
    }
  };

  const handleShare = async ({ studentIds, comment, fileUrl }) => {
    setIsSharing(true);

    try {
      // Create a document for each student

      const sharePromises = studentIds.map(async (studentId) => {
        const rubricData = {
          ...sharingRubric,
          sharedTo: studentId,
          sharedFrom: user.uid,
          comment: comment,
          fileUrl: fileUrl,
          shared: true,
          userId: studentId,
          createdAt: serverTimestamp(),
          originalRubricId: sharingRubric.id,
        };
        delete rubricData.id; // Remove the ID so a new one is generated

        // Create the rubric document
        const rubricDoc = await addDoc(collection(db, "rubrics"), rubricData);

        // Create notification
        await addDoc(collection(db, "notifications"), {
          type: "rubric_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          rubricData: {
            rubricId: rubricDoc.id,
            rubricName: sharingRubric.name,
            fileUrl: fileUrl,
            comment: comment,
          },
        });

        return rubricDoc;
      });

      await Promise.all(sharePromises);
      showSnackbar("Bedömningsmatrisen har delats med dina elever!", "success");
      setShareDialogOpen(false);
      setShareFriendsDialogOpen(false);
      setSharingRubric(null);
    } catch (error) {
      console.error("Error sharing rubric:", error);
      showSnackbar(
        "Ett fel uppstod när bedömningsmatrisen skulle delas.",
        "error",
      );
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      {rubrics.length === 0 ? (
        <Typography
          sx={{
            textAlign: "center",
            color: "text.secondary",
            width: { xs: "100%", md: "60%" },
            margin: "0 auto",
            mt: 4,
            mb: 4,
          }}
        >
          Du har inga egna bedömningsmatriser än. Klicka på "Ny matris" ovan för
          att skapa en. Eller välj en fördefinierad matris när du bedömer en ny
          text.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: "none" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Namn</TableCell>
                <TableCell>Skapad</TableCell>

                <TableCell align="right">Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rubrics.map((rubric) => (
                <TableRow
                  key={rubric.id}
                  hover
                  component={Link}
                  to={`/app/matris/visa/${rubric.id}`}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell>{rubric.name}</TableCell>
                  <TableCell>{formatDate(rubric.createdAt)}</TableCell>

                  <TableCell align="right">
                    <Tooltip title="Visa">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setViewingRubric(rubric);
                        }}
                      >
                        <VisibilityIcon sx={{ color: "#2196F3" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Redigera">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setEditingRubric(rubric);
                        }}
                      >
                        <EditIcon sx={{ color: "#4CAF50" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Dela med vänner">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSharingRubric(rubric);
                          setShareFriendsDialogOpen(true);
                        }}
                      >
                        <ShareIcon sx={{ color: "#7B68EE" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ta bort">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDelete(rubric.id);
                        }}
                      >
                        <DeleteIcon sx={{ color: "#F44336" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs */}
      <RubricsDialog
        open={Boolean(editingRubric)}
        onClose={() => setEditingRubric(null)}
        user={user}
        onRubricsUpdated={onRubricsUpdated}
        initialData={editingRubric}
        isEditing={true}
      />

      <RubricsDialog
        open={Boolean(viewingRubric)}
        onClose={() => setViewingRubric(null)}
        user={user}
        initialData={viewingRubric}
        watchOnly={true}
      />

      <LoadingBackdrop
        isOpen={isSharing}
        imagePath="/img/AI.png"
        loadingText="Delar bedömningsmatris..."
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setSharingRubric(null);
        }}
        onShare={handleShare}
        user={user}
      />

      <ShareDialog
        open={shareFriendsDialogOpen}
        onClose={() => {
          setShareFriendsDialogOpen(false);
          setSharingRubric(null);
        }}
        onShare={handleShare}
        user={user}
        shareWithFriends={true}
      />
    </>
  );
};

export default MyRubricsTable;
